import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Loader from 'react-loader-spinner';
import t from '@i18n/t';
import colors from '@utils/colors';
import storage from '@utils/storage';
import { isMobileApp } from '@utils/screenUtils';
import { tryAgain, useConnection } from '@utils/maintenance';
import { RootState } from '@state/types';
import MaterialIcon from './icons/MaterialIcon';

// A blip shorter than this doesn't show at all (Slack and Gmail wait too).
const SHOW_AFTER_MS = 2_000;
const ACCENT = '#a9a3ff';

/**
 * "You're offline. Reconnecting…" at the top while the student's internet is gone; the app stays usable underneath and
 * the bar goes once the connection is back (utils/maintenance.ts). A save lost meanwhile is said here too, in place of
 * the red message. Not in the phone app, which says it its own way.
 */
const OfflineBar = () => {
  const appLoaded = useSelector((state: RootState) => state.common.appLoaded);
  const { offline, checking, notSaved } = useConnection();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    setShown(false);
    if (!offline) return;
    const timeout = window.setTimeout(() => setShown(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(timeout);
  }, [offline]);

  // Before the app has loaded, the offline page says it instead.
  if (!offline || !appLoaded || isMobileApp || !(shown || notSaved)) return null;

  return (
    <Bar dark={storage.getTheme() !== 'light'} role="status" data-offline-bar>
      <MaterialIcon name="wifi_off" color={ACCENT} size={16} cursor="default" />
      <Text>{notSaved ? t.OFFLINE_NOT_SAVED : t.OFFLINE_RECONNECTING}</Text>
      <Action type="button" onClick={tryAgain} disabled={checking}>
        {checking ? <Loader type="Oval" color={ACCENT} height={14} width={14} /> : t.TRY_AGAIN}
      </Action>
    </Bar>
  );
};

export default OfflineBar;

// The look of the Undo toast (snackbarNotifications/Notifications.tsx bottomToast), at the top.
const Bar = styled.div<{ dark: boolean }>`
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 10px;
  background: ${props => (props.dark ? '#3B3858' : '#2E2B45')};
  border: 1px solid ${props => (props.dark ? '#4E4A6E' : '#2E2B45')};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  white-space: nowrap;
`;

const Text = styled.span`
  color: ${colors.white};
  font-size: 13px;
`;

const Action = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 18px;
  padding: 0;
  margin-left: 4px;
  border: none;
  background: none;
  color: ${ACCENT};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: progress;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;
